import { memo } from 'react';
import { useAppSelector } from '../_hooks/useAppSelector';
import type { Locale, MeasurementSystem } from '../../UserData/_stores/UserPrefsStore';

export type SizeDisplayType = 'physical' | 'digital';
export type SizeDisplaySourceUnit = 'px' | 'in' | 'cm';

export interface BaseSizeDisplayProps {
  sizes: number[]
}

interface PixelPhysicalSizeDisplayProps extends BaseSizeDisplayProps {
  displayType: 'physical'
  sourceUnit: 'px'
  dpi: number
}

interface UnitPhysicalSizeDisplayProps extends BaseSizeDisplayProps {
  displayType: 'physical'
  sourceUnit: 'in' | 'cm'
  dpi?: never
}

interface DigitalSizeDisplayProps extends BaseSizeDisplayProps {
  displayType: 'digital'
  sourceUnit: 'px'
  dpi?: never
}

export type SizeDisplayProps = PixelPhysicalSizeDisplayProps | UnitPhysicalSizeDisplayProps | DigitalSizeDisplayProps;

export interface PhysicalSizeDisplayOptions extends BaseSizeDisplayProps {
  dpi?: number
  locale: Locale
  measurementSystem: MeasurementSystem
  sourceUnit: SizeDisplaySourceUnit
}

export const getLocale = (locale: Locale) => {
  return locale.replace('_', '-');
}

export const formatMeasurementNumber = (value: number, locale: Locale, maximumFractionDigits: number) => {
  return new Intl.NumberFormat(getLocale(locale), {
    maximumFractionDigits,
    useGrouping: false,
  }).format(value);
}

export const formatDigitalSize = (sizes: number[], locale: Locale) => {
  const value = sizes
    .map(value => formatMeasurementNumber(Math.round(value), locale, 0))
    .join(' x ');

  return sizes.length === 1 ? value + 'px' : value + ' px';
}

export const getPhysicalSizeUnit = (measurementSystem: MeasurementSystem) => {
  return measurementSystem === 'metric' ? 'cm' : 'in';
}

export const getPhysicalSizes = (options: PhysicalSizeDisplayOptions) => {
  const sourceInches = (() => {
    if (options.sourceUnit === 'px') {
      if (options.dpi === undefined || options.dpi <= 0) {
        return undefined;
      }

      return options.sizes.map(value => value / options.dpi);
    }

    if (options.sourceUnit === 'cm') {
      return options.sizes.map(value => value / 2.54);
    }

    return options.sizes;
  })();

  if (sourceInches === undefined) {
    return undefined;
  }

  const conversion = options.measurementSystem === 'metric' ? 2.54 : 1;

  return sourceInches.map(value => value * conversion);
}

export const formatPhysicalSize = (options: PhysicalSizeDisplayOptions) => {
  const unit = options.measurementSystem === 'metric' ? 'cm' : 'in';
  const physicalSizes = getPhysicalSizes(options);

  if (physicalSizes === undefined) {
    return formatDigitalSize(options.sizes, options.locale);
  }

  return physicalSizes
    .map(value => formatMeasurementNumber(value, options.locale, 2))
    .join(' x ') + ' ' + unit;
}

function SizeDisplay(props: SizeDisplayProps) {
  const locale = useAppSelector(state => state.get('userData').get('prefs').get('lang'));
  const measurementSystem = useAppSelector(state => state.get('userData').get('prefs').get('measurementSystem'));
  const value = props.displayType === 'physical'
    ? formatPhysicalSize({
      sizes: props.sizes,
      sourceUnit: props.sourceUnit,
      measurementSystem,
      locale,
      dpi: props.dpi,
    })
    : formatDigitalSize(props.sizes, locale);

  return value;
}

export default memo(SizeDisplay);
